<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import DateFromDropdown from './DateFromDropdown.vue';
  import { DATE_FROM_TEXTS } from '../config/texts';
  import { normalizeDateFrom } from '../lib/dateRange';
  import { useDateFrom } from '../model/useDateFrom';
  import { Field, FieldSize } from '@/entities/searchForm';
  import { useOutsidePointer } from '@/shared/composables';
  import { type DateRange } from '@/shared/model';

  interface DateFromFieldProps {
    modelValue?: DateRange | null;
    size?: `${FieldSize}`;
    fieldLabel?: string;
  }

  const props = withDefaults(defineProps<DateFromFieldProps>(), {
    modelValue: null,
    size: FieldSize.M,
    fieldLabel: DATE_FROM_TEXTS.fieldLabel,
  });

  interface DateFromFieldEmits {
    (e: 'update:modelValue', value: DateRange): void;
    (e: 'manual'): void;
  }

  const emit = defineEmits<DateFromFieldEmits>();

  const model = computed<DateRange>({
    get: () => normalizeDateFrom(props.modelValue),
    set: (value) => emit('update:modelValue', value),
  });

  const core = useDateFrom({ model });

  const rootRef = ref<HTMLElement | null>(null);

  const onToggle = (): void => {
    core.toggle();
  };

  useOutsidePointer(rootRef, { active: core.controlActivated, handler: core.close });

  const onDateClick = (data: string) => {
    core.setAnchorFromISO(data);
    emit('manual');
  };

  const onSelectFlexible = (data: number) => {
    core.setFlexible(data);
    emit('manual');
  };

  defineExpose({
    validate: (): boolean => core.validate(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    ref="rootRef"
    class="date-from-field"
    data-testid="date-from-field"
  >
    <DateFromDropdown
      :anchor-iso="core.anchorIso.value"
      :flexible-for-calendar="core.flexibleForCalendar.value"
      :flexible="core.flexible.value"
      :min-date="core.minDate.value"
      :max-date="core.maxDate.value"
      :visible="core.controlActivated.value"
      @date-click="onDateClick"
      @select-flexible="onSelectFlexible"
      @close="core.close"
    >
      <template #reference>
        <Field
          :label="fieldLabel"
          :active="core.controlActivated.value"
          :size="size"
          value-testid="date-from-value"
          @click="onToggle"
        >
          <template #value>
            <Typo
              :variant="TypoVariant.BodyLRegular"
              tag="span"
            >
              {{ core.valueLabel.value }}
            </Typo>
          </template>
        </Field>
      </template>
    </DateFromDropdown>
  </div>
</template>

<style scoped lang="scss">
  .date-from-field {
    position: relative;
    width: 100%;
  }
</style>
